import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AssetGrid, AssetSelection } from "@/components/Backgrounds/library/AssetGrid";
import { LibraryGenerateDialog } from "@/components/Backgrounds/library/LibraryGenerateDialog";
import { activeFilterCount, clearPopoverFilters } from "@/components/Backgrounds/library/libraryFilterParams";
import { LibraryActions, LibraryToolbar, librarySearchBody } from "@/components/Backgrounds/library/LibraryToolbar";
import { SelectionBar } from "@/components/Backgrounds/library/SelectionBar";
import { useLibraryUpload } from "@/components/Backgrounds/library/useLibraryUpload";
import { useLibraryView } from "@/components/Backgrounds/library/useLibraryView";
import { useBackgroundLibrary } from "@/components/Backgrounds/useBackgroundLibrary";
import { useBackgroundRequests } from "@/components/Backgrounds/useBackgroundRequests";
import { PageWrapper } from "@/components/PageWrapper";
import { Button } from "@/components/ui";
import { BackgroundAsset } from "@/services/bedrock/dtos/BackgroundAsset";
import { useDynamicPromptsAccess } from "@/utils/useDynamicPromptsAccess";
import { BackgroundDetailDialog } from "./components/BackgroundDetailDialog";
import { CoachPromptStudioHeader } from "./components/CoachPromptStudioHeader";
import { HowBackgroundsWorkDialog } from "./help/HowBackgroundsWorkDialog";

const UPLOAD_NODE = "design-backgrounds/library";
const ASSET_PARAM = "asset";

function assetFromSearch(search: string): number | null {
  const id = Number(new URLSearchParams(search).get(ASSET_PARAM));
  return Number.isInteger(id) && id > 0 ? id : null;
}

interface GenerateState {
  open: boolean;
  prompt?: string;
}

/** The coach's Backgrounds tab: every background they can use, and their own to stock and tidy. */
export const BackgroundsPage: React.FC = () => {
  const { canEdit } = useDynamicPromptsAccess();
  // Filters and Only mine live in the URL, beside ?asset=.
  const { filters, mine, update } = useLibraryView("coach");
  const scope = mine ? "coach" : "all";
  const [selected, setSelected] = React.useState<AssetSelection>(new Map());
  const [generate, setGenerate] = React.useState<GenerateState>({ open: false });
  const [helpOpen, setHelpOpen] = React.useState(false);

  const history = useHistory();
  const location = useLocation();
  // The open background lives in the URL so a link or a reload lands on it.
  const openAssetId = assetFromSearch(location.search);
  const setOpenAssetId = (id: number | null) => {
    const params = new URLSearchParams(history.location.search);
    if (id == null) params.delete(ASSET_PARAM);
    else params.set(ASSET_PARAM, String(id));
    const search = params.toString();
    history.replace({ ...history.location, search: search ? `?${search}` : "" });
  };

  const body = librarySearchBody(filters, scope, null);
  // The grid's own query (same key, so no extra request), for previous and next in the detail dialog.
  const { assets, matches, isFetching } = useBackgroundLibrary(body);
  const openIndex = openAssetId == null ? -1 : assets.findIndex(asset => asset.id === openAssetId);
  // Read-only assistants may not list requests, and can't have made any.
  const { pending } = useBackgroundRequests({ scope: "mine", enabled: canEdit });
  // New backgrounds land in the coach's library, which both views include.
  const pendingNew = pending.filter(request => request.backgroundAssetId === null);

  // A selection belongs to one view of the library.
  React.useEffect(() => setSelected(new Map()), [scope]);

  const { upload, uploading } = useLibraryUpload({ node: UPLOAD_NODE, request: url => ({ url }) });

  const filtered = filters.q !== "" || activeFilterCount(filters, "coach") > 0;
  const empty = filtered ? (
    <>
      No backgrounds match.{" "}
      <Button
        variant="link"
        size="inline"
        onClick={() => update(current => ({ filters: { ...clearPopoverFilters(current.filters, "coach"), q: "" } }))}
      >
        Clear filters
      </Button>
    </>
  ) : mine ? (
    "No backgrounds of your own yet. Upload a photo or create one with AI."
  ) : (
    "No backgrounds yet. Upload a photo or create one with AI."
  );

  return (
    <PageWrapper hideSeparator fitViewport className="flex min-h-0 flex-1 flex-col gap-0">
      <CoachPromptStudioHeader
        tab="backgrounds"
        actions={
          <>
            <Button variant="outline" onClick={() => setHelpOpen(true)}>
              How backgrounds work
            </Button>
            {canEdit && (
              <LibraryActions
                multiple
                canWrite
                onUpload={files => void upload(files)}
                onGenerate={() => setGenerate({ open: true })}
                uploading={uploading}
              />
            )}
          </>
        }
      />
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:p-6" data-testid="coach-backgrounds">
        <LibraryToolbar
          mode="coach"
          filters={filters}
          onFiltersChange={next => update({ filters: next })}
          scope={scope}
          // Archived only exists in the coach's own library, so leaving it resets the choice.
          onScopeChange={next =>
            update(current => ({
              mine: next === "coach",
              filters: next === "coach" ? current.filters : { ...current.filters, visibility: ["active"] },
            }))
          }
          onIncludeArchivedChange={on =>
            update(current => ({
              filters: { ...current.filters, visibility: on ? ["active", "archived"] : ["active"] },
            }))
          }
          resultCount={isFetching ? null : matches}
        />
        <AssetGrid
          mode="coach"
          body={body}
          previewRatio={filters.previewRatio}
          selected={selected}
          onSelectedChange={setSelected}
          onOpen={setOpenAssetId}
          pendingRequests={pendingNew}
          selectable={(asset: BackgroundAsset) => canEdit && asset.coachId !== null}
          empty={empty}
        />
        {canEdit && <SelectionBar mode="coach" selected={selected} onSelectedChange={setSelected} />}
      </div>

      <BackgroundDetailDialog
        assetId={openAssetId}
        previewRatio={filters.previewRatio}
        canEdit={canEdit}
        onClose={() => setOpenAssetId(null)}
        onOpenAsset={setOpenAssetId}
        previousId={openIndex > 0 ? assets[openIndex - 1].id : null}
        nextId={openIndex >= 0 && openIndex < assets.length - 1 ? assets[openIndex + 1].id : null}
        onNewFromPrompt={prompt => setGenerate({ open: true, prompt })}
      />
      <LibraryGenerateDialog
        mode="coach"
        open={generate.open}
        onOpenChange={open => setGenerate(current => ({ ...current, open }))}
        initialPrompt={generate.prompt}
      />
      <HowBackgroundsWorkDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </PageWrapper>
  );
};
